import React, { useState } from "react";
import { Box, CircularProgress, Typography } from '@mui/material';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import { useHistory } from "react-router-dom";
import { withSnackbar } from "../../../utils/SnackbarHOC";
import { updateObjectStateValue } from "../../../utils/FunctionHelpers";
import { importContactsToSociete } from "../../../services/ContactService";
import Modal from "../../../components/Modal/Modal";
import { AppButton } from "../../../components/UI";
import { ImportFileStep, ImportStepper, ImportSummary, parseXlsx } from "../ImportWizardParts";
import Step1 from "./Components/Step1";
import Step3 from "./Components/Step3";

const steps = [
    'Choisir une société',
    'Importer le fichier',
    'Vérifier et importer',
];

const REQUIRED_HEADERS = ["titre", "nom", "prenom", "fonction"];

const cleanRow = (row) => (row.phoneNumbers !== undefined
    ? { ...row, phoneNumbers: String(row.phoneNumbers).replace(/HOME: |MOBILE: |WORK: | /gi, '') }
    : row);

const ContactImport = ({ snackbarShowMessage, setModalState, modalState }) => {
    const [importData, setImportData] = useState({});
    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState({});
    const history = useHistory();

    const resetStatesData = () => {
        setImportData({});
        setErrors({});
        setCurrentStep(0);
    };

    const handleClose = () => {
        if (importData.isLoading) return;
        setModalState({ open: false });
        resetStatesData();
    };

    const handleSubmit = () => {
        snackbarShowMessage("Importation des contacts...", "warning");
        updateObjectStateValue(setImportData, { isLoading: true });
        const payload = {
            societe: importData.societe.uuid,
            contactsToImport: importData.contactsToImport,
        };
        importContactsToSociete(payload).then(() => {
            snackbarShowMessage("Contacts a été ajouté dans la societe.");
            setModalState({ open: false });
            resetStatesData();
            history.push({
                pathname: `/societe/${importData.societe.uuid}/view`,
                search: '?tab=2',
            });
        }).catch(({ response }) => {
            snackbarShowMessage(response?.data?.error?.detail || "Une erreur est survenue.", "error", 4000);
        }).finally(() => {
            updateObjectStateValue(setImportData, { isLoading: false });
        });
    };

    const validate = async () => {
        const temp = {};
        if (currentStep === 0) {
            temp.societe = importData.societe ? "" : "Choisissez la société dans laquelle importer les contacts.";
        }
        if (currentStep === 1) {
            if (!importData.contactsFile) {
                temp.contactsFile = "Ajoutez le fichier Excel des contacts.";
            } else {
                updateObjectStateValue(setImportData, { isImporting: true });
                try {
                    const { rows, missing } = await parseXlsx(importData.contactsFile, REQUIRED_HEADERS, cleanRow);
                    updateObjectStateValue(setImportData, { contactsToImport: rows });
                    temp.contactsFile = missing.length
                        ? `Colonne${missing.length > 1 ? 's' : ''} manquante${missing.length > 1 ? 's' : ''} dans le fichier : ${missing.join(', ')}.`
                        : rows.length === 0 ? "Le fichier ne contient aucune ligne de contact." : "";
                } catch {
                    temp.contactsFile = "Impossible de lire ce fichier. Vérifiez qu'il s'agit bien d'un fichier Excel (.xlsx).";
                } finally {
                    updateObjectStateValue(setImportData, { isImporting: false });
                }
            }
        }
        setErrors(temp);
        return Object.values(temp).every((x) => x === "");
    };

    const handleNextStep = async () => {
        if (await validate()) setCurrentStep((s) => s + 1);
    };

    const rowsCount = importData.contactsToImport?.length || 0;

    const getStepContent = () => {
        switch (currentStep) {
            case 0:
                return <Step1 {...{ importData, setImportData, errors }} />;
            case 1:
                return (
                    <ImportFileStep
                        file={importData.contactsFile}
                        onFile={(file, error) => { updateObjectStateValue(setImportData, { contactsFile: file || undefined }); setErrors({ contactsFile: error || "" }); }}
                        requiredHeaders={REQUIRED_HEADERS}
                        templateName="modele_import_contacts.xlsx"
                        entityLabel="contacts"
                        error={errors.contactsFile}
                    />
                );
            case 2:
                return (
                    <>
                        <ImportSummary
                            count={rowsCount} entityLabel="contacts" targetLabel="la société" targetName={importData.societe?.nomSociete || importData.societe?.selectionName}
                            emptyEmails={(importData.contactsToImport || []).filter((c) => !String(c.email || '').trim()).length}
                        />
                        <Step3 {...{ importData, setImportData }} />
                    </>
                );
            default:
                return "Etape inconnue.";
        }
    };

    const actions = (
        <>
            <AppButton variant="secondary" onClick={currentStep > 0 ? () => setCurrentStep((s) => s - 1) : handleClose} disabled={importData.isLoading || importData.isImporting}>
                {currentStep > 0 ? "Retour" : "Annuler"}
            </AppButton>
            {currentStep < steps.length - 1 ? (
                <AppButton variant="primary" onClick={handleNextStep} loading={importData.isImporting}>
                    Continuer
                </AppButton>
            ) : (
                <AppButton variant="primary" onClick={handleSubmit} loading={importData.isLoading} disabled={!rowsCount}
                    startIcon={<CloudUploadOutlined fontSize="small" />}>
                    Importer {rowsCount} contact{rowsCount > 1 ? 's' : ''}
                </AppButton>
            )}
        </>
    );

    return (
        <Modal open={!!modalState.open} onClose={handleClose} title="Importer des contacts" maxWidth={currentStep === 2 ? 'lg' : 'md'} actions={actions}>
            <ImportStepper steps={steps} current={currentStep} errorStep={errors.contactsFile && currentStep === 1 ? 1 : undefined} />
            {importData.isImporting ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 5 }}>
                    <CircularProgress size={28} />
                    <Typography sx={{ fontSize: '0.88rem', color: '#64748b' }}>Lecture du fichier…</Typography>
                </Box>
            ) : getStepContent()}
        </Modal>
    );
};

export default withSnackbar(ContactImport);
